/**
 * React Query hooks for admin image operations
 * Admin-specific image data fetching with React Query
 */

import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  fetchAllUsersImages,
  fetchImageUsers,
  type ImageListParams,
  type ImageListResponse,
  type UserImage,
} from '@/api/imageApi';
import imageKeys from '@/api/querry_keys/imageKeys';
import adminKeys from '@/api/querry_keys/adminKeys';

/**
 * Hook for fetching all users' images (admin only)
 */
interface UseAllUsersImagesParams extends ImageListParams {
  isAdmin?: boolean;
}

export const useAllUsersImagesInfinite = (params: UseAllUsersImagesParams) => {
  return useInfiniteQuery({
    // Include ALL filter parameters in query key for proper cache invalidation
    queryKey: [...imageKeys.adminAll(), 'infinite', {
      status: params.status,
      user_email: params.user_email,
      sort_by: params.sort_by,
      limit: params.limit,
    }],
    queryFn: async ({ pageParam = 0 }) => {      
      const response = await fetchAllUsersImages({
        ...params,
        offset: pageParam,
      });
      
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: ImageListResponse) => {
      const nextOffset = (lastPage.offset || 0) + (lastPage.limit || 12);
      return lastPage.has_more ? nextOffset : undefined;
    },
    enabled: params.isAdmin !== false, // Default to enabled
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook for fetching image users list (admin only)
 */
export const useImageUsers = () => {
  return useQuery({
    queryKey: [...adminKeys.all, 'imageUsers'],
    queryFn: fetchImageUsers,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30,
  });
};

/**
 * Adapter hook that combines all admin image queries with backward-compatible interface
 * This provides the same interface as the old useAllUsersImages hook
 */
export const useAdminImagesAdapter = (params: UseAllUsersImagesParams) => {
  const queryClient = useQueryClient();
  const imagesQuery = useAllUsersImagesInfinite(params);
  const usersQuery = useImageUsers();

  // Flatten paginated images
  const images = imagesQuery.data?.pages.flatMap(page => page.images || []) || [];
  const allUsers = usersQuery.data?.users || [];

  // Calculate totals
  const totalCount = imagesQuery.data?.pages[0]?.total_count || 0;
  const totalUnfilteredCount = imagesQuery.data?.pages[0]?.total_unfiltered_count || 0;

  // Loading states
  const isLoading = imagesQuery.isPending || imagesQuery.isLoading;
  const isLoadingMore = imagesQuery.isFetchingNextPage;
  const hasMore = imagesQuery.hasNextPage || false;

  // Refetch functions
  const refetch = async () => {
    await Promise.all([
      imagesQuery.refetch(),
      usersQuery.refetch(),
    ]);
  };

  const loadMore = async () => {
    if (hasMore && !isLoadingMore) {
      await imagesQuery.fetchNextPage();
    }
  };

  // Helper function to update image pin status in cache
  const updateImageStatusAndFeedback = (
    image: UserImage,
    imageId: string,
    status: string,
    feedback?: string
  ) => {
    if (image.id !== imageId) return image;
    
    return {
      ...image,
      status,
      feedback: feedback ?? image.feedback,
      updated_at: new Date().toISOString()
    };
  };

  // Helper to map images in a page for status update
  const mapPageImagesForStatusUpdate = (page: any, imageId: string, status: string, feedback?: string) => ({
    ...page,
    images: page.images?.map((image: UserImage) =>
      updateImageStatusAndFeedback(image, imageId, status, feedback)
    ),
  });

  // Optimistic updates using React Query cache
  const optimisticUpdateImage = (imageId: string, status: string, feedback?: string) => {
    const queryKey = [...imageKeys.adminAll(), 'infinite', {
      status: params.status,
      user_email: params.user_email,
      sort_by: params.sort_by,
      limit: params.limit,
    }];

    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => 
          mapPageImagesForStatusUpdate(page, imageId, status, feedback)
        ),
      };
    });

    // Note: pinned images functionality has been removed
  };

  // Helper function to filter out deleted image
  const filterDeletedImage = (images: UserImage[] | undefined, imageId: string): UserImage[] => {
    return images?.filter((image: UserImage) => image.id !== imageId) ?? [];
  };

  const optimisticDeleteImage = (imageId: string) => {
    const queryKey = [...imageKeys.adminAll(), 'infinite', {
      status: params.status,
      user_email: params.user_email,
      sort_by: params.sort_by,
      limit: params.limit,
    }];

    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          images: filterDeletedImage(page.images, imageId),
          total_count: Math.max(0, (page.total_count ?? 0) - 1),
        })),
      };
    });

    // Note: pinned images functionality has been removed
  };

  return {
    // Data
    images,
    allUsers,
    
    // Counts
    totalCount,
    totalUnfilteredCount,
    
    // Loading states
    isLoading,
    isLoadingMore,
    isLoadingUsers: usersQuery.isPending || usersQuery.isLoading,
    hasMore,
    
    // Error
    error: imagesQuery.error ? String(imagesQuery.error) : null,
    
    // Functions
    refetch,
    loadMore,
    
    // Optimistic updates
    optimisticUpdateImage,
    optimisticDeleteImage,
  };
};
